import React, { FC, useCallback, useState } from 'react';
import PhotoAlbum from 'react-photo-album';
import { PhotoGalleryProps } from './PhotoGallery.types';
import { LightBox } from '@shared/components';
import { Box, ButtonV2, TooltipV2 } from '@withjoy/joykit';
import { ArrowDown } from '@withjoy/joykit/icons';
import { useFileDownload } from '@shared/utils/hooks/useDownloadFile';
import { HoverButtonContainer } from './PhotoGallery.styles';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useTranslation } from '@shared/core';
import { useMediaCollectionTelemetry } from '@apps/guest/routes/MediaCollection/MediaCollection.telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';

export const PhotoGallery: FC<PhotoGalleryProps> = props => {
  const { photos, layout = 'columns', onPhotoClick, columns, padding, spacing, collectionId } = props;
  const telemetry = useMediaCollectionTelemetry();
  const { eventInfo } = useEventInfo();
  const { downloadSingleFile } = useFileDownload({
    onSingleFileDownloadError: fileUrl => {
      telemetry.photoDownloadFailed(eventInfo?.eventId || '', collectionId || '', fileUrl);
    },
  });
  const [isPhotoDownloadEnabled] = useIsFeatureEnabled('websitePhotoDownloadGuestEnabled');
  const { tq } = useTranslation('websiteOverview');

  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(-1);

  const handlePhotoClick = useCallback(
    (index: number, mediaId: string, photoUrl: string) => {
      setSelectedPhotoIndex(index);
      onPhotoClick?.(mediaId, photoUrl);
    },
    [onPhotoClick],
  );

  const handleDownload = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, photoUrl: string) => {
      e.stopPropagation();
      downloadSingleFile(photoUrl);
    },
    [downloadSingleFile],
  );

  return (
    <>
      <PhotoAlbum
        layout={layout}
        photos={photos}
        columns={columns}
        padding={padding}
        spacing={spacing}
        onClick={({ index, photo }) => handlePhotoClick(index, photo.mediaId, photo.src)}
        renderPhoto={({ wrapperStyle, renderDefaultPhoto, photo }) =>
          isPhotoDownloadEnabled ? (
            <HoverButtonContainer>
              <TooltipV2
                content={tq`Download`}
                onMouseLeaveDelay={0}
              >
                <ButtonV2
                  position="absolute"
                  top={4}
                  right={4}
                  intent="neutral"
                  variant="solid"
                  backgroundColor="white"
                  height={7}
                  minWidth={7}
                  padding={0}
                  _hover={{
                    backgroundColor: 'white',
                  }}
                  _active={{
                    backgroundColor: 'white',
                  }}
                  startIcon={
                    <ArrowDown
                      color="black"
                      size={16}
                    />
                  }
                  onClick={e => handleDownload(e, photo.src)}
                  visibility="hidden"
                  title={tq`Download`}
                />
              </TooltipV2>
              <Box style={{ ...wrapperStyle, backgroundColor: 'rgba(211,211,211,0.60)' }}>{renderDefaultPhoto({ wrapped: true })}</Box>
            </HoverButtonContainer>
          ) : (
            <Box style={{ ...wrapperStyle, backgroundColor: 'rgba(211,211,211,0.60)' }}>{renderDefaultPhoto({ wrapped: true })}</Box>
          )
        }
      />
      <LightBox
        selectedPhotoIndex={selectedPhotoIndex}
        isOpen={selectedPhotoIndex >= 0}
        onClose={() => setSelectedPhotoIndex(-1)}
        photos={photos}
      />
    </>
  );
};

PhotoGallery.displayName = 'PhotoGallery';
